import { Col, Row } from "react-bootstrap"
import React from "react"
import { MatchScoreboard } from "@components/Match/MatchScoreboard/MatchScoreboard"
import { MatchMenu } from "@components/Match/Matchmenu/MatchMenu"
import { MatchAPI } from "../../../src/apis/MatchAPI"
import { getNavigationProps } from "@components/Navigation/getNavigationProps"
import { SEO } from "../../../src/types/next.js"
import { GetStaticPropsContext } from "next"
import { ParsedUrlQuery } from "querystring"
import InferNextPropsType from "infer-next-props-type"
import * as NextConstants from "../../../src/constants/next"
import { GroupAPI } from "../../../src/apis/GroupAPI"
import { TeamAPI } from "../../../src/apis/TeamAPI"
import { GroupStandingOld } from "@components/Competition/GroupStandingOld"
import { getMatchCanonicalData } from "utils/match"
import { GroupStandings } from "@components/Competition/GroupStandings"
import { simplifyNews } from "utils/news"

export default function MatchPage({
  matchCalciocomSlug,
  match,
  events,
  teamStandings,
  homeTeamLastMatchesPlayed,
  awayTeamLastMatchesPlayed,
  bettingParameterStandings,
  prediction,
}: InferNextPropsType<typeof getStaticProps>) {
  return (
    <>
      <Row className={"marginZero"}>
        <Col className="paddingZero">
          <div id="hs-content" className={"hs-border-div paddingZero"}>
            <MatchScoreboard
              match={match}
              events={events}
              homeTeamLastMatchesPlayed={homeTeamLastMatchesPlayed}
              awayTeamLastMatchesPlayed={awayTeamLastMatchesPlayed}
              prediction={prediction}
            />
            <div className="paddingOFive">
              <MatchMenu
                selected="classifica"
                isStandingsAvailable
                matchCalciocomSlug={matchCalciocomSlug}
                match={match}
              />
            </div>
            <GroupStandingOld
              competitionCalciocomSlug={match.competition?.calciocomSlug || null}
              seasonCalciocomSlug={match.season?.calciocomSlug || null}
              teamStandings={teamStandings}
              homeTeamId={match.homeTeam?.id}
              awayTeamId={match.awayTeam?.id}
            />
          </div>
        </Col>
      </Row>
      <Row className=" paddingFive">
        <Col>
          {teamStandings && teamStandings.length > 0 && (
            <div style={{ marginTop: "20px", marginBottom: "20px" }}>
              <GroupStandings
                competitionCalciocomSlug={match.competition?.calciocomSlug || null}
                seasonCalciocomSlug={match.season?.calciocomSlug || null}
                teamStandings={bettingParameterStandings}
                homeTeamId={match.homeTeam?.id}
                awayTeamId={match.awayTeam?.id}
              />
            </div>
          )}
        </Col>
      </Row>
    </>
  )
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" }
}

interface IParams extends ParsedUrlQuery {
  matchCalciocomSlug: string
}

export async function getStaticProps(context: GetStaticPropsContext<IParams>) {
  try {
    const { matchCalciocomSlug } = context.params!

    const [match] = await Promise.all([MatchAPI.getMatchByCalciocomSlug(matchCalciocomSlug)])
    const [
      events,
      homeTeamLastMatchesPlayed,
      awayTeamLastMatchesPlayed,
      teamStandings,
      canonicalData,
      navigationProps,
      bettingParameterStandings,
      prediction,
    ] = await Promise.all([
      MatchAPI.getMatchEvents(match.id),
      TeamAPI.getLastMatchesPlayed(match.homeTeamId!, match.groupId!),
      TeamAPI.getLastMatchesPlayed(match.awayTeamId!, match.groupId!),
      GroupAPI.getTeamStandings(match.groupId, match.turnId),
      getMatchCanonicalData(match, `/partite/${matchCalciocomSlug}/classifica`),
      getNavigationProps(match.competition?.id),
      GroupAPI.getTeamBettingParameterStandings(match.groupId),
      MatchAPI.getPredictionByMatchId(match.id),
    ])

    const turnName = match.turn?.name.split(".") ? `${match.turn?.name.split(".")[0]}º giornata` : match.turn?.name
    const title = `${match.homeTeam?.name} - ${match.awayTeam?.name}, ${match.competition?.name} ${match.season?.name}, ${turnName}: Classifica`
    const seo: SEO = {
      title: title,
      description: `Posizione in classifica di ${match.homeTeam?.name} ed ${match.awayTeam?.name} al termine della partita valida per la ${turnName} del Campionato di ${match.competition?.name} nella stagione ${match.season?.name}.`,
      keywords: [
        "Classifica",
        `${match.homeTeam?.name}`,
        `${match.awayTeam?.name}`,
        `${match.competition?.name}`,
        `${match.season?.name}`,
        `${match.turn?.name}`,
      ],
      ...canonicalData,
    }

    return {
      props: {
        matchCalciocomSlug: matchCalciocomSlug,
        match,
        events,
        teamStandings,
        homeTeamLastMatchesPlayed,
        awayTeamLastMatchesPlayed,
        navigationProps: navigationProps,
        title,
        seo,
        bettingParameterStandings,
        prediction: prediction ? simplifyNews(prediction) : null,
      },
      revalidate: NextConstants.REVALIDATE,
    }
  } catch (error) {
    console.error(error)
    return {
      notFound: true,
    }
  }
}
